import { useScrollReveal } from '../../hooks/useScrollReveal';
import LeadForm from './LeadForm';

export interface FinalCTAProps {
  eyebrow?: string;
  headline: string;
  subtext: string;
}

export default function FinalCTA({ eyebrow, headline, subtext }: FinalCTAProps) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-charcoal relative overflow-hidden py-28 lg:py-40">
      {/* Architectural background element */}
      <div
        className="absolute top-0 right-0 w-1/2 h-full opacity-[0.03]"
        style={{
          backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 47px, rgba(201,168,76,0.8) 47px, rgba(201,168,76,0.8) 48px)',
        }}
      />
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-gold/30 to-transparent" />

      <div className="relative z-10 mx-auto px-6 lg:px-12 xl:px-20">
        <div
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Left: CTA text */}
          <div className="lg:pt-4">
            {eyebrow && (
              <div className="flex items-center gap-4 mb-6">
                <span className="block w-8 h-px bg-gold flex-shrink-0" />
                <span className="text-gold text-[9px] font-semibold tracking-[0.38em] uppercase">
                  {eyebrow}
                </span>
              </div>
            )}

            <h2
              className="font-serif text-white leading-tight mb-7"
              style={{ fontSize: 'clamp(2rem, 3.5vw, 3.4rem)', fontWeight: 300 }}
            >
              {headline}
            </h2>
            <p className="text-white/45 text-base leading-[1.9] mb-12 max-w-md font-light">
              {subtext}
            </p>

            {/* Contact options */}
            <div className="space-y-5">
              <a
                href="tel:7279149002"
                className="flex items-center gap-5 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-gold/25 group-hover:border-gold group-hover:bg-gold/10 transition-all duration-300 flex-shrink-0">
                  <i className="ri-phone-line text-gold text-lg"></i>
                </div>
                <div>
                  <p className="text-[9px] text-white/30 tracking-[0.3em] uppercase mb-1">
                    Call Us Directly
                  </p>
                  <p className="text-white font-semibold tracking-wide group-hover:text-gold transition-colors duration-300">
                    727-914-9002
                  </p>
                </div>
              </a>

              <a
                href="mailto:Info@SELightingDesign.com"
                className="flex items-center gap-5 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-gold/25 group-hover:border-gold group-hover:bg-gold/10 transition-all duration-300 flex-shrink-0">
                  <i className="ri-mail-line text-gold text-lg"></i>
                </div>
                <div>
                  <p className="text-[9px] text-white/30 tracking-[0.3em] uppercase mb-1">
                    Email Us
                  </p>
                  <p className="text-white/80 text-sm group-hover:text-gold transition-colors duration-300">
                    Info@SELightingDesign.com
                  </p>
                </div>
              </a>
            </div>

            {/* Trust note */}
            <div className="mt-12 pt-10 border-t border-white/8">
              <div className="flex items-start gap-4">
                <i className="ri-check-double-line text-gold flex-shrink-0 mt-0.5"></i>
                <p className="text-white/35 text-xs leading-relaxed">
                  Complimentary assessment for commercial properties in Tampa Bay and throughout Florida. No obligation.
                </p>
              </div>
            </div>
          </div>

          {/* Right: Lead Form */}
          <div>
            <LeadForm />
          </div>
        </div>

        {/* Google Map — full width below */}
        <div
          className={`mt-16 lg:mt-20 transition-all duration-700 delay-300 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="block w-8 h-px bg-gold flex-shrink-0" />
            <span className="text-gold text-[9px] font-semibold tracking-[0.38em] uppercase">
              Find Us — Tampa Bay, FL
            </span>
          </div>
          <div className="relative w-full overflow-hidden border border-white/10" style={{ height: '400px' }}>
            <iframe
              title="Southeast Lighting & Design — Tampa Bay, FL"
              src="https://maps.google.com/maps?cid=17733934542755152632&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <p className="text-white/30 text-xs tracking-wide mt-4">
            Serving Tampa Bay and throughout Florida. Nationwide commercial lighting experience available.
          </p>
        </div>
      </div>
    </section>
  );
}
